import store from '@/store'
import { ScenePreset } from '../classes'
import { TweetAsset } from '@/types/assets'
import { Result } from '@/modules/errorHandling/results'
import { dummyTweetUrl, getImportedTweetData } from '@/modules/socialmedia/utils'
import { Cast } from '@/modules/casts/classes'

export const removePresetFolder = async (presetId: string, folderId: string) => {
  const result = await store.dispatch.events.updateCurrentCast((cast) => {
    delete cast.asset_groups[folderId]
    return cast.updatePreset(presetId, (preset) => preset.removeAssetGroup(folderId))
  })
  result.logIfError(`Could not remove folder [${ folderId }] from preset [${ presetId }]`)
}

export const togglePresetFolder = async (presetId: string, folderId: string, enabled: boolean) => {
  const result = await store.dispatch.events.updateCurrentCast((cast) => {
    cast.asset_groups[folderId] = enabled
    return cast.updatePreset(presetId, (preset) => preset.toggleAssetGroup(folderId, enabled))
  })
  result.logIfError(`Could not toggle folder [${ folderId }] in preset [${ presetId }]`)
}

export const isAssetActive = (scenePreset: ScenePreset|null, assetId: string): boolean => {
  if (scenePreset === null) return false
  return !!scenePreset.contents[assetId]?.active
}

export const importDummyTweet = async (
  castId: string|undefined, presetId: string, teamId: string|null
): Promise<Result<string, string>> => {
  return await importSingleTweet(castId, presetId, dummyTweetUrl, teamId)
}

export const importSingleTweet = async (
  castId: string|undefined, presetId: string, urlString: string, teamId: string|null
): Promise<Result<string, 'url_error'|'cast_error'|'team_error'|'database'>> => {
  if (teamId === null) return Result.fail('team_error', 'Invalid team: teamId is null')
  if (castId === undefined) return Result.fail('cast_error', 'Invalid castId: castId is undefined')
  const folderId = await store.dispatch.assets.setupImportedTweetsFolder(castId)
  if (folderId.isFailure) return folderId.convert()
  const tweet = getImportedTweetData(teamId, folderId.value, urlString)
  if (tweet.isFailure) return tweet.convert()
  const assetId = await store.dispatch.assets.createTweetAsset(tweet.value)
  const result = await store.dispatch.events.updateCurrentCast((cast) => {
    return cast.updatePreset(presetId, (preset) => preset.addAssetGroup(folderId.value))
  })
  result.logIfError(`Could not add folder [${ folderId.value }] to preset [${ presetId }]`)
  await addTweet(presetId, [{ ...tweet.value, id: assetId }], assetId)
  return Result.success(tweet.value.tweet_id)
}

export const addTweet = async (presetId: string, assets: TweetAsset[], assetId: string, isActive?: boolean) => {
  const result = await store.dispatch.events.updateCurrentCast((cast) => {
    const asset = assets.find((asset) => asset.id === assetId)
    return cast.updatePreset(presetId, (preset) => {
      if (!asset) return Result.fail('not_found', `Asset [${ assetId }] not found`)
      return preset.addTweet(asset, isActive)
    })
  })
  result.logIfError(`Could not add asset [${ assetId }] to preset [${ presetId }]`)
}

export const removeTweet = async (presetId: string, assetId: string) => {
  const result = await store.dispatch.events.updateCurrentCast((cast) => {
    return cast.updatePreset(presetId, (preset) => preset.removeTweet(assetId))
  })
  result.logIfError(`Could not remove asset [${ assetId }] from preset [${ presetId }]`)
}

export const addOrRemoveTweet = async (scenePreset: ScenePreset|null, assets: TweetAsset[], assetId: string) => {
  if (scenePreset === null) return
  const hasAsset = scenePreset.contents[assetId]
  if (hasAsset) await removeTweet(scenePreset.id, assetId)
  else await addTweet(scenePreset.id, assets, assetId)
}

// Sorted in desc order because the z-index for scene content is derived
// from the global layer id and we want the left most global layer to be on top.
export const getGlobalScenePresets = (cast: Cast|null, isHiddenIncluded: boolean = false) => {
  if (cast === null) return []
  return Object.values(cast.scenePresets ?? {})
    .filter((preset) => preset.isGlobalPreset && (isHiddenIncluded || !preset.isHiddenGlobalPreset))
    .sort((a, b) => a.id > b.id ? -1 : 1)
}
